/** core libraries & third party packages */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";

/** icons */
import { BsCart4 } from "react-icons/bs";
import { FaArrowRightLong } from "react-icons/fa6";
import { RiDeleteBinLine } from "react-icons/ri";

/** component */
import ProductCarousel from "../../components/core/productCardCarousel/ProductCarousel";

/** services */
import { getProductById } from "../../services/api";

/** custom hooks & query & context */
import { useCartQuery } from "../../hooks/useReactQuery";

/** utils */
import { handleGetUserFromLocalStorage } from "../../utils/common";

/** style */
import "./Cart.scss";

/** interfaces */
import { IUser } from "../../Interfaces/user";
import { IOrder } from "../../Interfaces/order";
import { CartItem } from "../../Interfaces/cart";
import { IProduct } from "../../Interfaces/product";

interface ICartWithProducts extends CartItem {
  product: IProduct | undefined;
}
const Cart = () => {
  const [productQuantity, setProductQuantity] = useState({
    productId: "",
    quantity: 0,
  });
  const [cartsWithProduct, setCartsWithProduct] =
    useState<ICartWithProducts[]>();
  const [isPending, setIsPending] = useState(false);
  const [deleteCartItem, setDeleteCartItem] = useState<ICartWithProducts>();

  const currentUser = JSON.parse(
    handleGetUserFromLocalStorage() as string
  ) as IUser;

  // query start
  const {
    Prefetch: prefetchGetAllCart,
    updateMutation,
    deleteMutation,
  } = useCartQuery();
  const getAllCarts = prefetchGetAllCart(currentUser?._id);
  // query end

  // calculate cart summary;
  const orderSummary = useMemo(() => {
    const subtotal =
      cartsWithProduct?.reduce(
        (acc, item) => acc + item.price * item.quantity,
        0
      ) ?? 0;

    const discount = subtotal * 0;
    const deliveryFee = 100;
    const total = subtotal - discount + deliveryFee;

    return {
      subtotal: +subtotal.toFixed(2),
      discount: +discount.toFixed(2),
      deliveryFee: +deliveryFee.toFixed(2),
      total: +total.toFixed(2),
    };
  }, [cartsWithProduct]);

  // cart quantity;
  useEffect(() => {
    if (cartsWithProduct !== undefined) {
      const updatedCartWithProductData = cartsWithProduct.map((item) => {
        if (item._id === productQuantity.productId) {
          return {
            ...item,
            quantity: Math.max(item.quantity + productQuantity.quantity, 1),
          };
        }
        return item;
      });

      if (updatedCartWithProductData && updatedCartWithProductData.length > 0) {
        handleUpdateCartItem(updatedCartWithProductData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productQuantity]);

  useEffect(() => {
    const handleFetchAllCarts = async () => {
      if (getAllCarts.data?.data !== undefined) {
        const productIds = getAllCarts.data.data.map(
          (cart: CartItem) => cart.productId
        );
        const productDetails = await handleFetchProductsByIds(productIds);
        // console.log("productDetails", productDetails);
        const cartWithProducts = getAllCarts.data.data.map((item: CartItem) => {
          const product = productDetails.find(
            (product: IProduct) => product._id === item.productId
          );
          return { ...item, product };
        });
        setCartsWithProduct(cartWithProducts);
        // console.log("cartWithProducts", cartWithProducts);
      }
    };
    handleFetchAllCarts();
  }, [getAllCarts.data?.data]);

  const handleFetchProductsByIds = async (productIds: string[]) => {
    const productRequests = productIds.map((id) => {
      return getProductById(id).then((res) => res.data);
    });
    const productDetails = await Promise.all(productRequests);
    return productDetails.map((product) => product);
  };

  const handleUpdateCartItem = async (
    updatedCartWithProductData: ICartWithProducts[]
  ) => {
    setIsPending(true);
    // removed products and _id from cartWithProducts
    const updatedCartData = updatedCartWithProductData?.map(
      ({ _id, product, ...item }: ICartWithProducts) => item
    );

    const payload: IOrder = {
      userId: currentUser._id,
      products: updatedCartData,
    };
    updateMutation
      .mutateAsync(payload)
      .then((res) => {
        console.log("res", res);
        toast.success("Cart Item updated");
        getAllCarts.refetch();
        setIsPending(false);
      })
      .catch((err) => {
        console.error("Failed to update cart item");
        toast.error("Failed to update cart item, please try again");
        setIsPending(false);
      });
  };

  useEffect(() => {
    if (cartsWithProduct !== undefined && cartsWithProduct.length !== 0)
      handleDeleteCartItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteCartItem]);

  const handleDeleteCartItem = useCallback(async () => {
    const cartItem = cartsWithProduct
      ?.filter((cartItem) => cartItem._id === deleteCartItem?._id)
      .map(({ _id, quantity, price, product, variantImages, ...cart }) => ({
        ...cart,
      }));

    if (cartItem === undefined) {
      return;
    }
    const deleteCartPayload = {
      ...cartItem[0],
      userId: currentUser._id,
    };

    deleteMutation
      .mutateAsync(deleteCartPayload)
      .then(async () => {
        toast.success("Cart Item deleted");
        setIsPending(false);
        const data = await getAllCarts.refetch();
        console.log("data", data);
      })
      .catch((err) => {
        console.log("err", err);
        toast.error("Failed to update cart item, please try again");
        setIsPending(false);
      });
  }, [deleteCartItem, cartsWithProduct]);

  // const handleDeleteCartItem = async () => {
  //   const cartItem = cartsWithProduct
  //     ?.filter((cartItem) => cartItem._id === deleteCartItem?._id)
  //     .map(({ _id, quantity, price, product, variantImages, ...cart }) => ({
  //       ...cart,
  //     }));

  //   if (cartItem === undefined) {
  //     return;
  //   }
  //   const deleteCartPayload = {
  //     ...cartItem[0],
  //     userId: currentUser._id,
  //   };

  //   deleteMutation
  //     .mutateAsync(deleteCartPayload)
  //     .then(async () => {
  //       toast.success("Cart Item deleted");
  //       setIsPending(false);
  //       const data = await getAllCarts.refetch();
  //       console.log("data", data);
  //     })
  //     .catch((err) => {
  //       console.log("err", err);
  //       toast.error("Failed to update cart item, please try again");
  //       setIsPending(false);
  //     });
  // };

  return (
    <div className="cart_page w-[96%]">
      <div className="cart_page_container">
        <div className="flex gap-10 pt-10 justify-between lg:flex-row flex-col">
          <div
            className={`${
              cartsWithProduct !== undefined && cartsWithProduct.length !== 0
                ? "lg:w-[60%]"
                : "lg:w-[100%]"
            } w-[100%]`}
          >
            <div className=" w-[100%] border border-[var(--whitish-gray-color)]  bg-white  rounded-md px-4 py-2">
              <div className=" font-medium text-[24px]">Your Bag</div>
              {cartsWithProduct !== undefined ? (
                <>
                  {cartsWithProduct.length !== 0 ? (
                    cartsWithProduct?.map((carts) => {
                      return (
                        <div className="flex gap-2  border-b border-[var(--whitish-gray-color)] py-4">
                          <div className=" w-[75px] h-[75px]">
                            <img
                              src={carts.variantImages?.[0]}
                              alt="product"
                              loading="lazy"
                              className=" object-cover w-full h-full rounded"
                            />
                          </div>
                          <div className="flex flex-col w-full">
                            <div className="flex justify-between w-full items-center">
                              <div className=" font-medium sm:text-[15px] text-[13px]">
                                {carts.product?.name}
                              </div>

                              <div
                                className="text-[var(--danger-text-color)] font-semibold text-[20px] cursor-pointer"
                                onClick={() => {
                                  setDeleteCartItem(carts);
                                }}
                              >
                                <RiDeleteBinLine />
                              </div>
                            </div>
                            <div className="sm:text-[14px] text-[12px] font-normal">
                              Size: <span>{carts.size}</span>
                            </div>
                            <div className="flex justify-between">
                              <div className="font-medium sm:text-[20px] text-[17px]">
                                ₹ {carts.price}
                              </div>
                              <div className=" bg-[var(--whitish-gray-color)]  select-none h-fit px-5 w-fit flex gap-4 items-center rounded-[20px]">
                                <button
                                  className="text-[22px] cursor-pointer"
                                  disabled={isPending || carts.quantity === 1}
                                  onClick={() => {
                                    setProductQuantity({
                                      productId: carts._id as string,
                                      quantity: -1,
                                    });
                                  }}
                                >
                                  -
                                </button>
                                <div className="text-[14px]">
                                  {carts.quantity}
                                </div>
                                <button
                                  className="text-[20px] cursor-pointer"
                                  disabled={isPending}
                                  onClick={() => {
                                    setProductQuantity({
                                      productId: carts._id as string,
                                      quantity: 1,
                                    });
                                  }}
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="h-[200px] flex justify-center items-center flex-col gap-5">
                      <div>
                        <BsCart4 size={30} />
                      </div>
                      Your cart is empty. Start adding your favorite items !
                    </div>
                  )}
                </>
              ) : (
                <>Loading...</>
              )}
            </div>
          </div>
          {cartsWithProduct?.length !== 0 && cartsWithProduct !== undefined && (
            <div className="lg:w-[40%] w-[100%] rounded-md p-4 h-fit bg-white">
              <div className=" text-[20px] font-medium">Order Summary</div>
              <div className="grid  grid-cols-2 justify-between w-full pt-2">
                <div className="flex flex-col gap-3">
                  <div className="text-[14px] font-normal text-[var(--text-gray-color)]">
                    Subtotal
                  </div>
                  <div className="text-[14px] font-normal text-[var(--text-gray-color)]">
                    Discount (0%)
                  </div>
                  <div className="text-[14px] font-normal text-[var(--text-gray-color)]">
                    Delivery Fee
                  </div>
                  <div className="text-[16px] font-medium">Total</div>
                </div>
                <div className="flex justify-end flex-col items-end gap-3">
                  <div className="text-[14px] font-normal">
                    ₹{orderSummary.subtotal}
                  </div>
                  <div className="text-[14px] font-normal">
                    ₹{orderSummary.discount}
                  </div>
                  <div className="text-[14px] font-normal">
                    ₹{orderSummary.deliveryFee}
                  </div>
                  <div className="font-medium text-[16px]">
                    ₹{orderSummary.total}
                  </div>
                </div>
              </div>
              <Link
                to={"/checkout"}
                className="bg-[var(--black-color)] button w-full relative text-center group py-3 rounded-3xl mt-5 flex cursor-pointer  justify-center items-center gap-2"
              >
                <button
                  type="submit"
                  className=" outline-none border-none text-white text-[15px]"
                >
                  Go To Checkout
                </button>
                <div>
                  <FaArrowRightLong color="#fff" />
                </div>
              </Link>
            </div>
          )}
        </div>
        <div className="pt-[80px]">
          <ProductCarousel />
        </div>
      </div>
    </div>
  );
};

export default Cart;
